import {api} from "../../lib/api";
import {debounce, projectsDecks} from "../../lib/utils";
import {waitForResult, waitForResultPromise} from "../../lib/wait-for-result";
import {DeckOpt} from "./categories/deck";
import {searchCategories} from "./search-categories";
import {pronounceSafeSeq} from "../../lib/sequences";
import {GetSuggestionContext, Suggestion} from "./search-types";
import {Project, ProjectId} from "../../cdx-models/Project";
import {CardSuggestion} from "./search-utils";

type Task<T = any> = Pick<Suggestion, "category" | "isFilter"> & {
  trigger: {value: string; emptyHelp: string} | null;
  type: "immediate" | "debounced";
  catLabel: string;
  renderSuggestion: (value: T) => React.ReactNode;
  nonPublic?: boolean;
  get: (
    opts: GetSuggestionContext & {
      forced?: boolean;
    }
  ) => {value: T; key: string | number | null}[];
};

export type SearchTask = Task;

const allTasks: Task[] = [
  ...searchCategories.map(
    ({key, getSuggestions, valueToKey, renderSuggestion, label, nonPublic, trigger}) =>
      ({
        trigger: trigger || null,
        type: "immediate",
        category: key,
        isFilter: true,
        renderSuggestion,
        catLabel: label,
        nonPublic,
        get: ({input, root, projects, projectIds, forced}) =>
          (getSuggestions({input, root, projects, projectIds, viaTrigger: forced}) || []).map(
            (value) => ({
              value,
              // why is this cast needed?
              key: valueToKey(value as never),
            })
          ),
      }) as Task
  ),
  {
    trigger: {value: "gd:", emptyHelp: "Found no deck with this name"},
    type: "immediate",
    category: "gotoDecks",
    catLabel: "Go to deck",
    renderSuggestion: (value) => <DeckOpt id={value} pa="4px" />,
    isFilter: false,
    get: ({input, root, projectIds}) =>
      projectsDecks({root, projectIds})
        .filter((d) => (process.env.REACT_APP_MODE === "open" ? d.project.isPublic : true))
        .filter((d) => d.$meta.get("title", "").toLowerCase().indexOf(input) > -1)
        .map((d) => ({value: d.id, key: d.id})),
  },
  {
    trigger: {value: "gad:", emptyHelp: "Found no deck with this name"},
    type: "immediate",
    category: "gotoAnyDecks",
    catLabel: "Go to deck in any project",
    renderSuggestion: (value) => <DeckOpt id={value} pa={0} />,
    isFilter: false,
    get: ({input, root, forced}) =>
      forced
        ? root.account.$meta
            .find("anyDecks", {isDeleted: false})
            .filter((d) => (process.env.REACT_APP_MODE === "open" ? d.project.isPublic : true))
            .filter((d) => d.$meta.get("title", "").toLowerCase().indexOf(input) > -1)
            .map((d) => ({value: d.id, key: d.id}))
        : [],
  },
  {
    trigger: {value: "gc:", emptyHelp: "Found no card with this title"},
    type: "debounced",
    category: "gotoCards",
    catLabel: "Go to card",
    renderSuggestion: (value) => <CardSuggestion id={value} />,
    isFilter: false,
    get: ({input, root, projectIds}) =>
      root.account.$meta
        .find("cards", {
          title: {op: "contains", value: input},
          $or: [{deck: {projectId: projectIds}}, {deckId: null}],
          visibility: "default",
          ...(process.env.REACT_APP_MODE === "open" && {isPublic: true}),
          $order: "-lastUpdatedAt",
          $limit: 10,
        })
        .map((c) => ({value: c.cardId, key: c.cardId})),
  },
  {
    trigger: {value: "gac:", emptyHelp: "Found no card with this title"},
    type: "debounced",
    category: "gotoAnyCards",
    catLabel: "Go to card in any project",
    renderSuggestion: (value) => <CardSuggestion id={value} />,
    isFilter: false,
    get: ({input, root, forced}) =>
      forced
        ? root.account.$meta
            .find("cards", {
              title: {op: "contains", value: input},
              visibility: "default",
              ...(process.env.REACT_APP_MODE === "open" && {isPublic: true}),
              $order: "-lastUpdatedAt",
              $limit: 10,
            })
            .map((c) => ({value: c.cardId, key: c.cardId}))
        : [],
  },
  {
    trigger: {value: "$", emptyHelp: "Found no card with this id"},
    type: "debounced",
    category: "gotoCardById",
    catLabel: "Go to card",
    renderSuggestion: (value) => <CardSuggestion id={value} />,
    isFilter: false,
    get: ({input, root}) =>
      input.length >= 3 && input.length <= 5
        ? root.account.$meta
            .find("cards", {
              accountSeq: pronounceSafeSeq.seqToInt(input),
              ...(process.env.REACT_APP_MODE === "open" && {isPublic: true}),
            })
            .map((c) => ({value: c.cardId, key: c.cardId}))
        : [],
  },
];
const groupTasksByType = (tasks: Task[]) =>
  tasks.reduce(
    (m, t) => {
      (m[t.type] = m[t.type] || []).push(t);
      return m;
    },
    {} as Record<Task["type"], Task[]>
  );

const publicTasks = allTasks.filter((t) => !t.nonPublic);

type ImmediateTaskParams = {
  tasks: Task[];
  input: string;
  projectIds: ProjectId[];
  addOnceLoaded: (suggestions: Suggestion[]) => void;
  forced: boolean;
};

const immediateTasks = ({tasks, input, projectIds, addOnceLoaded, forced}: ImmediateTaskParams) => {
  if (tasks.length === 0) {
    return {suggestions: [], isLoaded: true, cancelAsyncLoading: () => {}};
  }
  let retVal:
    | undefined
    | {
        suggestions: Suggestion[];
        isLoaded: boolean;
        cancelAsyncLoading: null | undefined | (() => void);
      };
  let maybeWaitUnsub: undefined | (() => void);
  maybeWaitUnsub = waitForResult(
    () => {
      const currentProjects = projectIds
        .map((id) => api.getModel({modelName: "project", id})!)
        .filter(Boolean);
      const list: Suggestion[] = [];
      const root = api.getRoot();
      tasks.forEach((task) => {
        const {category, isFilter, renderSuggestion, catLabel, get, trigger} = task;
        const suggestions = get({
          input,
          root,
          projects: currentProjects,
          projectIds,
          forced,
        });
        if (suggestions.length > 0 || forced) {
          list.push({
            category,
            entries: suggestions.length > 15 ? suggestions.slice(0, 15) : suggestions,
            isFilter,
            renderSuggestion,
            catLabel,
            trigger,
          });
        }
      });
      return list;
    },
    (suggestions) => {
      // happens either immediately before findSuggestions returns, or async
      retVal = {suggestions, isLoaded: true, cancelAsyncLoading: null};
      if (maybeWaitUnsub) addOnceLoaded(suggestions);
    }
  );
  return retVal || {suggestions: [], isLoaded: false, cancelAsyncLoading: maybeWaitUnsub};
};

type DebouncTaskParams = {
  tasks: Task[];
  input: string;
  projectIds: ProjectId[];
  addOnceLoaded: (suggestions: Suggestion[]) => void;
  forced: boolean;
};

type DebouncedFn = (<T>(fn: () => Promise<T>) => Promise<T>) & {cancel: () => void};
const performDebounced: DebouncedFn = debounce((fn) => fn(), 250);

type DebTaskRetval = {
  suggestions: Suggestion[];
  isLoaded: boolean;
  cancelAsyncLoading: null | undefined | (() => void);
};

const getTasks = (filterFn: undefined | ((t: Task) => boolean)) => {
  let tasks = process.env.REACT_APP_MODE === "open" ? publicTasks : allTasks;
  if (filterFn) tasks = tasks.filter(filterFn);
  return {tasksWithTrigger: tasks.filter((t) => t.trigger), tasksByType: groupTasksByType(tasks)};
};

const debounceTasks = ({
  tasks,
  input,
  projectIds,
  addOnceLoaded,
  forced,
}: DebouncTaskParams): DebTaskRetval => {
  if (tasks.length === 0) {
    return {suggestions: [], isLoaded: true, cancelAsyncLoading: () => {}};
  }
  let isCancelled = false;
  performDebounced(() =>
    waitForResultPromise(() => {
      if (isCancelled) return [];
      const root = api.getRoot();
      const projects = projectIds
        .map((id) => api.getModel({modelName: "project", id})!)
        .filter(Boolean);
      return tasks.map((info) => {
        return {entries: info.get({input, root, projectIds, forced, projects}), info};
      });
    })
  ).then((entriesAndInfo) => {
    let suggestions = [];
    entriesAndInfo.forEach(({entries, info}) => {
      const {category, isFilter, renderSuggestion, catLabel, trigger} = info;
      if (entries.length || forced) {
        suggestions.push({
          entries: entries.length > 15 ? entries.slice(0, 15) : entries,
          category,
          catLabel,
          renderSuggestion,
          isFilter,
          trigger,
        });
      }
      if (!isCancelled) addOnceLoaded(suggestions);
    });
  });
  const debTasks = {
    suggestions: [],
    isLoaded: false,
    cancelAsyncLoading: () => {
      performDebounced.cancel();
      isCancelled = true;
    },
  };
  return debTasks;
};

export function findSuggestions(
  rawInput: string,
  projects: Project[],
  addOnceLoaded: (opts: {suggestions: Suggestion[]; isLoaded: boolean}) => void,
  filterTasks?: (task: Task) => boolean
) {
  const projectIds = projects.map((p) => p.$meta.get("id", null)!).filter(Boolean);
  const handleLoaded = () => {
    const suggestions = tasks.reduce((prev, t) => [...prev, ...t.suggestions], [] as Suggestion[]);
    const isLoaded = tasks.reduce((prev, t) => prev && t.isLoaded, true);
    addOnceLoaded({suggestions, isLoaded});
  };

  const {tasksWithTrigger, tasksByType} = getTasks(filterTasks);

  // if user entered e.g. `gd:`
  const triggerTask = tasksWithTrigger.find(
    (t) => t.trigger && rawInput.indexOf(`${t.trigger.value}`) === 0
  ) as (Task & {trigger: NonNullable<Task["trigger"]>}) | undefined;

  const {immediate, debounced} = triggerTask ? groupTasksByType([triggerTask]) : tasksByType;

  const input = (
    triggerTask ? rawInput.slice(triggerTask.trigger.value.length) : rawInput
  ).toLowerCase();

  const immTask = immediateTasks({
    tasks: immediate || [],
    input,
    projectIds,
    addOnceLoaded: (sugg) => {
      immTask.suggestions = sugg;
      immTask.isLoaded = true;
      immTask.cancelAsyncLoading = null;
      handleLoaded();
    },
    forced: Boolean(triggerTask),
  });

  const debTasks = debounceTasks({
    tasks: debounced || [],
    input,
    projectIds,
    addOnceLoaded: (sugg) => {
      debTasks.suggestions = sugg;
      debTasks.isLoaded = true;
      debTasks.cancelAsyncLoading = null;
      handleLoaded();
    },
    forced: Boolean(triggerTask),
  });
  const tasks = [immTask, debTasks];

  const unsubs = () => tasks.forEach((t) => t.cancelAsyncLoading && t.cancelAsyncLoading());
  return {
    suggestions: tasks.reduce((prev, t) => [...prev, ...t.suggestions], [] as Suggestion[]),
    isLoaded: tasks.reduce((prev, t) => prev && t.isLoaded, true),
    cancelAsyncLoading: unsubs,
  };
}
